import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Container,
  Card,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaClipboardList, FaHistory } from "react-icons/fa";
import moment from "moment";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { useTypeConfig } from "../../../utils/useTypeConfig";
import IssueStockModal from "../IssueStockModal";

const ServiceViewModel = ({
  show,
  handleClose,
  servicerequestId,
  servicerequest,
}) => {
  const [serviceLogs, setServiceLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showIssueStockModal, setShowIssueStockModal] = useState(false);
  const { labels } = useTypeConfig();

  useEffect(() => {
    if (!show || !servicerequestId) return;

    const fetchLogs = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${BASE_URL}/api/service-request-logs/${servicerequestId}/`,
          { headers: HEADERS },
        );

        setServiceLogs(response.data);
      } catch (err) {
        console.error("Error fetching service logs:", err);
        setError("Failed to load service request logs.");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [show, servicerequestId]);

  const getBadgeColor = (status) => {
    switch (status) {
      case "Dispatched":
        return "#ffc107";
      case "Approved":
        return "#28a745";
      case "Submitted":
        return "#6c757d";
      case "Rejected":
        return "#dc3545";
      default:
        return "#343a40";
    }
  };
  const renderChanges = (changes) => {
    if (!changes || typeof changes !== "object") return "—";

    const validChanges = Object.entries(changes).filter(
      ([, v]) => v?.new !== null && v?.new !== "" && v?.new !== undefined,
    );

    if (validChanges.length === 0) return "—";

    return (
      <ul className="mb-0 ps-3">
        {validChanges.map(([field, value], idx) => (
          <li key={idx}>
            <strong>{field}</strong>:{" "}
            <span className="text-muted">{value.previous ?? "—"}</span> →{" "}
            <span className="text-success">{value.new}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      scrollable
      dialogClassName="custom-modern-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100 d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            🛠 Service Request #{servicerequest?.id || ""}
          </span>
          <Button
              size="sm"
              variant="warning"
              onClick={() => setShowIssueStockModal(true)}
              className="ms-2"
              disabled={servicerequest?.status === "Submitted"}
            >
              Issue Stock
            </Button>

          <span
            className="badge"
            style={{
              backgroundColor: getBadgeColor(servicerequest?.status),
              padding: "6px 12px",
              color: "#fff",
            }}
          >
            {servicerequest?.status}
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
            <p className="mt-2">Loading service request data...</p>
          </div>
        ) : error ? (
          <p className="text-danger text-center">{error}</p>
        ) : (
          <Container fluid>
            <Card className="mb-3 shadow-sm">
              <Card.Body className="small">
                <Row>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Created At:</strong>{" "}
                      {moment(servicerequest?.created_at).format(
                        "DD-MM-YYYY",
                      ) || "—"}
                    </p>

                    <p className="mb-1">
                      <strong>Created By:</strong>{" "}
                      {servicerequest?.created_by
                        ? `${servicerequest.created_by.first_name || ""} ${
                            servicerequest.created_by.last_name || ""
                          }`.trim()
                        : "—"}
                    </p>

                    <p className="mb-1">
                      <strong>Ticket ID:</strong>{" "}
                      {servicerequest?.ticket?.id || "—"}
                    </p>

                    <p className="mb-1">
                      <strong>{labels?.customerName}:</strong>{" "}
                      {servicerequest?.ticket?.customerDetails?.customer_name ||
                        "—"}
                    </p>

                    <p className="mb-1">
                      <strong>{labels?.subCustomerName}:</strong>{" "}
                      {servicerequest?.ticket?.subCustomerDetails
                        ?.customer_name || "—"}
                    </p>

                    <p className="mb-1">
                      <strong>Assigned To:</strong>{" "}
                      {servicerequest?.ticket?.assignTo
                        ? `${servicerequest.ticket.assignTo.first_name || ""} ${
                            servicerequest.ticket.assignTo.last_name || ""
                          }`.trim()
                        : "—"}
                    </p>
                  </Col>

                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Region:</strong>{" "}
                      {servicerequest?.ticket?.region || "—"}
                    </p>

                    <p className="mb-1">
                      <strong>Phone:</strong>{" "}
                      {servicerequest?.ticket?.phone_number || "—"}
                    </p>

                    <p className="mb-1">
                      <strong>Required Materials:</strong>{" "}
                      {servicerequest?.material_required
                        ?.map((m) => m.name)
                        .join(", ") || "—"}
                    </p>

                    <p className="mb-1">
                      <strong>Dispatched Location:</strong>{" "}
                      {servicerequest?.where_to_dispatched || "—"}
                    </p>

                    <p className="mb-1">
                      <strong>Hub Address:</strong>{" "}
                      {servicerequest?.hub_address || "—"}
                    </p>

                    <p className="mb-1">
                      <strong>Remark:</strong>{" "}
                      {servicerequest?.approved_remark ||
                        servicerequest?.dispatched_remark ||
                        "—"}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            <Card className="shadow-sm">
              <Card.Body>
                <h5 className="text-primary mb-3">
                  <FaHistory className="me-2" />
                  Service Activity Log
                </h5>

                {serviceLogs?.logs?.length > 0 ? (
                  <div style={{ overflowX: "auto" }}>
                    <table className="inventory-table w-100">
                      <thead>
                        <tr>
                          <th style={{ minWidth: "150px" }}>Updated By</th>
                          <th style={{ minWidth: "170px" }}>Date & Time</th>
                          <th style={{ minWidth: "120px" }}>Action</th>
                          <th style={{ minWidth: "250px" }}>Changes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {serviceLogs.logs.map((log, idx) => (
                          <tr key={idx}>
                            <td>{log.updated_by || "—"}</td>
                            <td>
                              {moment(log.updated_on).format(
                                "DD-MM-YYYY hh:mm A",
                              )}
                            </td>
                            <td>
                              <Badge bg="secondary">{log.action || "—"}</Badge>
                            </td>
                            <td>{renderChanges(log.changes)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted text-center mb-0">
                    No service activity available.
                  </p>
                )}
              </Card.Body>
            </Card>
          </Container>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
    {showIssueStockModal && (
        <IssueStockModal
          show={showIssueStockModal}
          handleClose={() => setShowIssueStockModal(false)}
          servicerequest={servicerequest}
        />
      )}
      </>
  );
};

export default ServiceViewModel;
